import React, { useState, useEffect } from "react";
import {
  CheckCircle,
  UploadCloud,
  ChevronRight,
  Image as ImageIcon,
  AlertCircle,
  X,
} from "lucide-react";
import { LEAVE_TYPES } from "../../../config/constants";
import {
  uploadProofImage,
  calculateLeaveStats,
  formatDurationDisplay,
  generateTimeOptions,
  splitDateTime,
  getNextWorkdayDefaults,
} from "../../../utils/leaveHelpers";

export default function LeaveForm({
  user,
  userProfile,
  targetAnnualInfo,
  leaves = [],
  editTargetUid,
  editId,
  onSubmit,
  initialData,
  onCancel,
  isEditing,
  proxyOptions = [],
  proxyEmails = {},
  proxyOptionsLoading = false,
  isAdminLeave = false,
}) {
  const defaults = getNextWorkdayDefaults();
  const [formData, setFormData] = useState(
    initialData || {
      type: "personal",
      startDate: defaults.start,
      endDate: defaults.end,
      reason: "",
      proxyUid: "",
      otherType: "",
      proofImage: null, // Storage URL；舊資料若為 base64 也能直接顯示
    }
  );

  const [calcResult, setCalcResult] = useState({ days: 0, hours: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isValidTime, setIsValidTime] = useState(true);

  const timeOptions = generateTimeOptions();
  const applicantUid = isEditing ? editTargetUid : user?.uid;
  const availableProxies = proxyOptions.filter(p => p.uid !== applicantUid);
  const proxyUid = availableProxies.some(p => p.uid === formData.proxyUid)
    ? formData.proxyUid : (availableProxies[0]?.uid || "");

  useEffect(() => {
    if (!formData.startDate || !formData.endDate) return;

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) {
      setIsValidTime(false);
      setCalcResult({ days: 0, hours: 0 });
    } else {
      setIsValidTime(true);
      setCalcResult(calculateLeaveStats(formData.startDate, formData.endDate));
    }
  }, [formData.startDate, formData.endDate]);

  const handleDateTimeChange = (field, part, value) => {
    const currentIso = formData[field];
    const [curDate, curTime] = splitDateTime(currentIso);
    let newIso =
      part === "date" ? `${value}T${curTime}` : `${curDate}T${value}`;

    if (field === "startDate" && part === "date") {
      const [endD, endT] = splitDateTime(formData.endDate);
      const newEndIso = `${value}T${endT}`;
      setFormData((prev) => ({
        ...prev,
        startDate: newIso,
        endDate: newEndIso,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: newIso }));
    }
  };

  // [新增] 即時處理圖片上傳 -> 多段壓縮為 Base64 存入 Firestore
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const result = await uploadProofImage(file, user?.uid);
      setFormData((prev) => ({ ...prev, proofImage: result.url }));
    } catch (error) {
      console.error("圖片上傳失敗:", error);
      alert("圖片上傳失敗：" + (error?.message || "請重試"));
    } finally {
      setIsUploading(false);
    }
  };

  // [新增] 移除圖片
  const removeImage = (e) => {
    e.stopPropagation(); // 防止觸發 file input
    e.preventDefault();
    setFormData((prev) => ({ ...prev, proofImage: null }));
    // 重置 input value 以便能重複上傳同一張
    const fileInput = document.getElementById("proof-upload");
    if (fileInput) fileInput.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!isAdminLeave && !proxyUid) return alert("目前沒有其他在職正式員工可擔任代理人，請聯絡管理員。");

    if (!isValidTime) {
      alert("結束時間必須晚於開始時間");
      return;
    }

    if (
      formData.type === "annual" &&
      calcResult.days > targetAnnualInfo.remaining
    )
      return alert(
        `錯誤：特休剩餘天數不足！(剩餘 ${targetAnnualInfo.remaining} 天)`
      );

    if (formData.type === "menstrual") {
      const menstrualLimit =
        LEAVE_TYPES.MENSTRUAL.annualLimitDays || 3;
      const hireDateStr = targetAnnualInfo?.hireDate;
      if (!hireDateStr) {
        return alert("錯誤：尚未設定到職日，無法計算生理假年度上限。請至設定頁填寫到職日後再申請。");
      }
      const hire = new Date(hireDateStr);
      if (isNaN(hire.getTime())) {
        return alert("錯誤：到職日格式不正確，無法計算生理假年度上限。");
      }
      const targetUid =
        isEditing && editTargetUid ? editTargetUid : user?.uid;
      const leaveStart = new Date(formData.startDate);

      // 以到職日為基準，找出本次請假所屬的「年資年度」區間
      const cycleStart = new Date(hire);
      cycleStart.setFullYear(
        hire.getFullYear() +
          (leaveStart.getFullYear() - hire.getFullYear())
      );
      if (leaveStart < cycleStart) {
        cycleStart.setFullYear(cycleStart.getFullYear() - 1);
      }
      const cycleEnd = new Date(cycleStart);
      cycleEnd.setFullYear(cycleStart.getFullYear() + 1);

      const usedDays = leaves
        .filter((l) => {
          if (l.uid !== targetUid) return false;
          if (l.type !== "menstrual") return false;
          if (l.id === editId) return false;
          const ls = new Date(l.startDate);
          return ls >= cycleStart && ls < cycleEnd;
        })
        .reduce((sum, l) => sum + (Number(l.days) || 0), 0);

      const totalIfApproved = usedDays + calcResult.days;
      const fmt = (d) =>
        `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
      if (totalIfApproved > menstrualLimit) {
        return alert(
          `錯誤：本年度（${fmt(cycleStart)} ~ ${fmt(new Date(cycleEnd.getTime() - 86400000))}）生理假已用 ${usedDays} 天，本次申請 ${calcResult.days} 天，將超過年度上限 ${menstrualLimit} 天。`
        );
      }
    }

    setIsSubmitting(true);

    // 這裡 proofImage 已經在 handleImageUpload 處理好了，直接使用
    const submissionData = {
      ...formData,
      proxyUid,
      proxyEmail: "",
      typeName:
        Object.values(LEAVE_TYPES).find((t) => t.id === formData.type)?.label ||
        formData.type,
      days: calcResult.days,
      hours: calcResult.hours,
    };

    try {
      await onSubmit(submissionData);
    } catch (error) {
      alert("假單儲存失敗：" + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="paper-form bg-white p-5 rounded-2xl shadow-sm border border-slate-100 space-y-5"
    >
      <div className="bg-gradient-to-r from-slate-50 to-white p-4 rounded-xl border border-slate-100 flex justify-between items-center">
        <span className="text-slate-500 text-sm font-bold">特休剩餘</span>
        <span className="text-blue-600 text-2xl font-bold font-mono">
          {targetAnnualInfo.remaining}{" "}
          <span className="text-sm text-slate-400">天</span>
        </span>
      </div>

      <div>
        <label className="block text-sm font-bold text-slate-700 mb-2">
          請選擇假別
        </label>
        <select
          className="w-full border border-slate-200 rounded-xl p-3 bg-white outline-none focus:ring-2 focus:ring-blue-100 transition-all text-slate-700 font-medium"
          value={formData.type}
          onChange={(e) => {
            const newType = e.target.value;
            const typeDef = Object.values(LEAVE_TYPES).find(
              (t) => t.id === newType
            );
            setFormData((prev) => ({
              ...prev,
              type: newType,
              // 切換到不需要證明的假別時，清空已上傳的圖片避免誤送
              proofImage: typeDef?.requireProof ? prev.proofImage : null,
            }));
          }}
        >
          {Object.values(LEAVE_TYPES).map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      <div
        className={`border p-4 rounded-xl relative mt-4 space-y-4 transition-colors ${
          isValidTime
            ? "border-slate-200 bg-slate-50/30"
            : "border-red-200 bg-red-50/30"
        }`}
      >
        <div
          className={`absolute -top-3 left-4 px-2 text-xs font-bold border rounded-full shadow-sm ${
            isValidTime
              ? "bg-white text-blue-500 border-blue-100"
              : "bg-red-100 text-red-500 border-red-200"
          }`}
        >
          時間設定
        </div>

        <div className="leave-time-row grid grid-cols-3 gap-2 items-center">
          <label className="text-xs font-bold text-slate-500 col-span-3 mb-1">
            開始時間
          </label>
          <input
            type="date"
            className="col-span-2 w-full border border-slate-200 rounded-lg p-3 bg-white outline-none focus:ring-2 focus:ring-blue-100"
            value={splitDateTime(formData.startDate)[0]}
            onChange={(e) =>
              handleDateTimeChange("startDate", "date", e.target.value)
            }
          />
          <select
            className="col-span-1 w-full border border-slate-200 rounded-lg p-3 bg-white outline-none focus:ring-2 focus:ring-blue-100 text-center font-mono"
            value={splitDateTime(formData.startDate)[1]}
            onChange={(e) =>
              handleDateTimeChange("startDate", "time", e.target.value)
            }
          >
            {timeOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div className="leave-time-row grid grid-cols-3 gap-2 items-center">
          <label className="text-xs font-bold text-slate-500 col-span-3 mb-1">
            結束時間
          </label>
          <input
            type="date"
            className="col-span-2 w-full border border-slate-200 rounded-lg p-3 bg-white outline-none focus:ring-2 focus:ring-blue-100"
            value={splitDateTime(formData.endDate)[0]}
            onChange={(e) =>
              handleDateTimeChange("endDate", "date", e.target.value)
            }
          />
          <select
            className="col-span-1 w-full border border-slate-200 rounded-lg p-3 bg-white outline-none focus:ring-2 focus:ring-blue-100 text-center font-mono"
            value={splitDateTime(formData.endDate)[1]}
            onChange={(e) =>
              handleDateTimeChange("endDate", "time", e.target.value)
            }
          >
            {timeOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {!isValidTime && (
          <div className="flex items-center gap-2 text-red-500 text-xs font-bold bg-white p-2 rounded-lg border border-red-100 shadow-sm animate-pulse">
            <AlertCircle size={14} /> 結束時間必須晚於開始時間
          </div>
        )}
      </div>

      <div className="animate-fade-in space-y-5">
        {!isAdminLeave && <div>
          <label className="block text-sm font-bold text-slate-700 mb-2" htmlFor="leave-proxy-email">
            職務代理人 <span className="text-red-500">*</span>
          </label>
          <select
            id="leave-proxy-email"
            required
            className="w-full border border-slate-200 rounded-lg p-3"
            value={proxyUid}
            disabled={proxyOptionsLoading}
            onChange={(e) => setFormData({ ...formData, proxyUid: e.target.value })}
          >
            {!availableProxies.length && <option value="">{proxyOptionsLoading ? "載入中" : "無可用代理人"}</option>}
            {availableProxies.map(p => <option key={p.uid} value={p.uid}>{p.fullName || p.name || "員工"}</option>)}
          </select>
          <div className="mt-2 text-xs text-slate-600 break-all">
            通知信箱：{proxyEmails[proxyUid] || "尚未取得通知信箱"}
          </div>
        </div>}
        <div className="flex justify-between items-center bg-blue-50 p-4 rounded-xl border border-blue-100">
          <span className="text-sm font-bold text-blue-800">預計請假</span>
          <span className="font-bold text-blue-600 text-lg">
            {isValidTime
              ? formatDurationDisplay(calcResult.days, calcResult.hours)
              : "--"}
          </span>
        </div>

        <div>
          <label className="block text-sm font-bold text-slate-700 mb-2">
            請假事由 <span className="text-red-500">*</span>
          </label>
          <textarea
            className="w-full border border-slate-200 rounded-xl p-4 outline-none focus:ring-2 focus:ring-blue-100 min-h-[100px]"
            placeholder="請詳細說明原因..."
            required
            value={formData.reason}
            onChange={(e) =>
              setFormData({ ...formData, reason: e.target.value })
            }
          ></textarea>
        </div>

        {/* [修改] 圖片上傳區塊 */}
        {formData.type === "sick" && (
          <div
            className={`border-2 border-dashed rounded-2xl p-4 transition-all relative group text-center ${
              formData.proofImage
                ? "border-green-300 bg-green-50"
                : "border-blue-200 bg-slate-50 hover:bg-blue-50 cursor-pointer"
            }`}
          >
            {!formData.proofImage ? (
              <>
                <input
                  type="file"
                  id="proof-upload"
                  accept="image/*"
                  disabled={isUploading}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 disabled:cursor-wait"
                  onChange={handleImageUpload}
                />
                <div className="flex flex-col items-center gap-3 py-2">
                  <div className="bg-white p-3 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                    {isUploading ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    ) : (
                      <UploadCloud className="w-6 h-6 text-blue-500" />
                    )}
                  </div>
                  <div>
                    <span className="text-sm font-bold text-slate-700 group-hover:text-blue-600 block">
                      {isUploading ? "上傳中..." : "點擊上傳就診證明"}
                    </span>
                    <span className="text-xs text-slate-400 mt-1 block">
                      JPG / PNG，最大 2MB（自動壓縮）
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="relative">
                <img
                  src={formData.proofImage}
                  alt="Preview"
                  className="max-h-48 mx-auto rounded-lg shadow-sm object-contain bg-white"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 z-20"
                >
                  <X size={16} />
                </button>
                <div className="mt-2 text-green-600 text-xs font-bold flex items-center justify-center gap-1">
                  <CheckCircle size={12} /> 圖片已準備
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-3 pt-4 border-t border-slate-100">
          {isEditing && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-slate-100 text-slate-600 py-3.5 rounded-xl font-bold hover:bg-slate-200 transition-colors"
            >
              取消
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting || isUploading || !isValidTime}
            className="flex-1 bg-blue-600 text-white py-3.5 rounded-xl font-bold disabled:bg-slate-300 disabled:cursor-not-allowed hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all transform active:scale-95"
          >
            {isSubmitting
              ? "處理中..."
              : isUploading
              ? "等待圖片上傳..."
              : isEditing
              ? "更新假單"
              : "確認送出"}
          </button>
        </div>
      </div>
    </form>
  );
}
